import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pin, Trash2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const COLOR_PALETTE = [
  { name: "Amarelo", value: "#FEF3C7" },
  { name: "Verde", value: "#D1FAE5" },
  { name: "Azul", value: "#DBEAFE" },
  { name: "Rosa", value: "#FCE7F3" },
  { name: "Laranja", value: "#FED7AA" },
  { name: "Lilás", value: "#E9D5FF" },
];

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "short" });
}

/**
 * Painel embedável de notas para um cliente ou job.
 *
 * Props:
 *  - entityType: "client" | "job"
 *  - entityId: id da entidade dona das notas
 *  - title (opcional): cabeçalho do bloco
 *  - canEdit (default true): exibe botões de adicionar/excluir/fixar
 */
export function EntityNotes({ entityType, entityId, title, canEdit = true }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null); // {id?, title, content, color, pinned}

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/notes?entity_type=${entityType}&entity_id=${entityId}`,
      );
      setNotes(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  async function save() {
    if (!draft) return;
    const body = {
      title: (draft.title || "").trim(),
      content: (draft.content || "").trim(),
      color: draft.color || COLOR_PALETTE[0].value,
      pinned: !!draft.pinned,
      entity_type: entityType,
      entity_id: entityId,
    };
    if (!body.title && !body.content) {
      toast.error("Escreva algum conteúdo");
      return;
    }
    try {
      if (draft.id) {
        await api.patch(`/notes/${draft.id}`, body);
      } else {
        await api.post(`/notes`, body);
      }
      setDraft(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  async function togglePin(n) {
    try {
      await api.patch(`/notes/${n.id}`, { ...n, pinned: !n.pinned });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function remove(n) {
    if (!window.confirm("Excluir esta nota?")) return;
    try {
      await api.delete(`/notes/${n.id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid={`entity-notes-${entityType}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
          <StickyNote size={16} className="text-amber-600" />
          {title || (entityType === "client" ? "Notas deste cliente" : "Notas deste atendimento")}
          {notes.length > 0 && (
            <span className="text-xs text-slate-500">({notes.length})</span>
          )}
        </div>
        {canEdit && !draft && (
          <button
            type="button"
            onClick={() =>
              setDraft({
                title: "",
                content: "",
                color: COLOR_PALETTE[0].value,
                pinned: false,
              })
            }
            data-testid="entity-note-add"
            className="text-xs font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
          >
            <Plus size={12} /> Adicionar
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-slate-500">Carregando...</div>
      ) : notes.length === 0 && !draft ? (
        <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-3">
          Nenhuma nota ainda. Use para guardar lembretes
          {entityType === "client" ? " (código de portão, preferências, alergias…)" : " (observações do dia, materiais extras…)"}
          .
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div
              key={n.id}
              className="rounded-md p-2 text-xs relative group"
              style={{ background: n.color || "#FEF3C7" }}
              data-testid={`entity-note-${n.id}`}
            >
              {n.pinned && (
                <Pin
                  size={12}
                  className="absolute top-1.5 right-1.5 text-amber-700"
                />
              )}
              {n.title && (
                <div className="font-bold text-slate-900 mb-0.5 pr-5 break-words">
                  {n.title}
                </div>
              )}
              {n.content && (
                <div className="text-slate-800 whitespace-pre-wrap break-words">
                  {n.content}
                </div>
              )}
              <div className="flex items-center justify-between mt-1.5 text-[10px] text-slate-600">
                <span>
                  {n.user_name ? `${n.user_name} · ` : ""}
                  {formatRelative(n.updated_at)}
                </span>
                {canEdit && (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => setDraft(n)}
                      className="px-1 hover:bg-black/10 rounded"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => togglePin(n)}
                      title={n.pinned ? "Desafixar" : "Fixar"}
                      className="p-1 hover:bg-black/10 rounded"
                    >
                      <Pin
                        size={10}
                        className={n.pinned ? "fill-current" : ""}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(n)}
                      title="Excluir"
                      className="p-1 hover:bg-black/10 rounded text-red-700"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {draft && (
        <div
          className="mt-2 rounded-md p-3 border border-slate-200"
          style={{ background: draft.color || "#FEF3C7" }}
          data-testid="entity-note-editor"
        >
          <input
            autoFocus
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Título (opcional)"
            className="w-full bg-transparent border-b border-black/20 px-1 py-1 font-bold text-slate-900 text-sm placeholder:text-slate-500 focus:outline-none"
            maxLength={120}
            data-testid="entity-note-title"
          />
          <textarea
            value={draft.content}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            placeholder="Escreva uma observação..."
            rows={3}
            className="w-full bg-transparent px-1 py-1 mt-1 text-xs text-slate-800 placeholder:text-slate-500 focus:outline-none resize-none"
            maxLength={10000}
            data-testid="entity-note-content"
          />
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {COLOR_PALETTE.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setDraft({ ...draft, color: c.value })}
                title={c.name}
                className={`w-5 h-5 rounded-full ring-1 ring-black/20 ${
                  draft.color === c.value ? "ring-2 ring-black" : ""
                }`}
                style={{ background: c.value }}
              />
            ))}
            <label className="inline-flex items-center gap-1 text-[11px] ml-2">
              <input
                type="checkbox"
                checked={!!draft.pinned}
                onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })}
              />
              <Pin size={10} /> Fixar
            </label>
            <div className="ml-auto flex gap-1.5">
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="text-xs px-2 py-1 rounded hover:bg-black/10"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={save}
                data-testid="entity-note-save"
                className="text-xs px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-700 font-semibold"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
